import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { Button } from '@/ui/base/button';
import {
  PERFIS_ACESSO_MOCK,
  USUARIOS_POR_PERFIL_MOCK,
  type UsuarioDoPerfilMock,
} from './dadosMock';

const PER_PAGE = 10;

/**
 * `/admin/perfis/:id/usuarios` — ONDA-CA2. `/admin/perfis` (a lista de
 * perfis) ainda não existe (fase 8): esta tela é alcançável direto pela URL,
 * com dado mockado (ver dadosMock.ts). Quando a lista real nascer, ela passa
 * a linkar para cá.
 */
export function PerfilUsuariosPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const tempo = window.setTimeout(() => setCarregando(false), 200);
    return () => window.clearTimeout(tempo);
  }, []);

  const perfil = id ? PERFIS_ACESSO_MOCK[id] : undefined;
  const usuarios = useMemo<UsuarioDoPerfilMock[]>(
    () => (id ? (USUARIOS_POR_PERFIL_MOCK[id] ?? []) : []),
    [id],
  );
  // Hooks não podem vir depois do retorno antecipado abaixo (react-hooks/rules-of-hooks) —
  // por isso `filtrados` é calculado aqui, mesmo que `perfil` ainda não exista.
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return usuarios;
    return usuarios.filter((u) => `${u.nome} ${u.cargo}`.toLowerCase().includes(termo));
  }, [usuarios, busca]);

  if (carregando) {
    return <Spinner size="lg" className="min-h-[50vh]" />;
  }

  if (!perfil) {
    return (
      <ErrorState
        variant="resource-not-found"
        title="Perfil não encontrado"
        description="Este perfil de acesso não existe ou foi removido."
        primaryAction={{ label: 'Voltar para perfis', onClick: () => navigate('/admin/perfis') }}
      />
    );
  }

  const colunas: DataTableColumn<UsuarioDoPerfilMock & { id: string }>[] = [
    { id: 'nome', header: 'Nome', cell: (u) => <span className="font-medium">{u.nome}</span> },
    {
      id: 'cargo',
      header: 'Cargo',
      cell: (u) => <span className="text-sm text-muted-foreground">{u.cargo}</span>,
    },
    {
      id: 'vinculado_em',
      header: 'Vinculado em',
      cell: (u) => (
        <span className="text-xs text-muted-foreground">
          {new Date(u.vinculadoEm).toLocaleString('pt-BR')}
        </span>
      ),
      sortKey: (u) => new Date(u.vinculadoEm).getTime(),
    },
  ];

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome ou cargo…',
      flex: true,
    },
  ];

  const pageItems = filtrados
    .slice((pagina - 1) * PER_PAGE, pagina * PER_PAGE)
    .map((u) => ({ ...u, id: u.userId }));

  return (
    <div className="space-y-4">
      <PageHeader title={`Usuários do perfil: ${perfil.nome}`} description={perfil.descricao}>
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/perfis')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Voltar para perfis
        </Button>
      </PageHeader>

      <FilterBar
        fields={campos}
        values={{ busca }}
        onChange={(_chave, valor) => {
          setBusca(valor);
          setPagina(1);
        }}
        onClear={() => {
          setBusca('');
          setPagina(1);
        }}
      />

      <DataTable
        columns={colunas}
        data={pageItems}
        total={filtrados.length}
        page={pagina}
        perPage={PER_PAGE}
        onPageChange={setPagina}
        onRowClick={(u) => navigate(`/admin/usuarios?user=${u.userId}`)}
        ariaLabel="Usuários do perfil"
        emptyIcon={Users}
        emptyTitle="Nenhum usuário vinculado"
        emptyDescription="Este perfil ainda não está atribuído a nenhum usuário."
      />
    </div>
  );
}

export default PerfilUsuariosPage;
